import clsx from "clsx";
import { Box, Typography } from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import { DiagramInfoField } from "../types";
import { useStyles } from "./styles";

export const getTextDirection = (language: string) =>
  (language || "hw").startsWith("hw") ? "rtl" : "ltr";

type InfoCardProps = {
  titleKey: string;
  fields: DiagramInfoField[];
  columnFlow?: boolean;
  singleColumn?: boolean;
  fullWidth?: boolean;
  autoHeight?: boolean;
};

type FieldsListProps = {
  fields: DiagramInfoField[];
  t: (key: string) => string;
  resolveValue: (value: string) => string;
  styles: ReturnType<typeof useStyles>;
  allowWrap?: boolean;
};

const FieldsList = ({
  fields,
  t,
  resolveValue,
  styles,
  allowWrap = false,
}: FieldsListProps) => (
  <Box className={styles.fieldsList}>
    {fields.map((field) => (
      <React.Fragment key={field.labelKey}>
        <Typography component="span" className={styles.fieldLabel}>
          {t(field.labelKey)}
        </Typography>
        <Typography
          component="span"
          className={clsx(styles.fieldValue, allowWrap && styles.fieldValueWrap)}
        >
          {resolveValue(field.value)}
        </Typography>
      </React.Fragment>
    ))}
  </Box>
);

export const InfoCard = ({
  titleKey,
  fields,
  columnFlow = false,
  singleColumn = false,
  fullWidth = false,
  autoHeight = false,
}: InfoCardProps) => {
  const { t, i18n } = useTranslation();
  const styles = useStyles();
  const textDirection = getTextDirection(i18n.language);

  const resolveValue = (value: string) =>
    value.startsWith("WORK_ORDER.") ? t(value) : value;

  const splitIndex = Math.ceil(fields.length / 2);
  const firstColumnFields = fields.slice(0, splitIndex);
  const secondColumnFields = fields.slice(splitIndex);

  const renderFieldsList = (columnFields: DiagramInfoField[], allowWrap = false) => (
    <FieldsList
      fields={columnFields}
      t={t}
      resolveValue={resolveValue}
      styles={styles}
      allowWrap={allowWrap}
    />
  );

  const renderFields = () => {
    if (columnFlow) {
      return (
        <Box className={styles.fieldsGridColumnFlow}>
          <Box className={styles.fieldsColumn}>
            {renderFieldsList(firstColumnFields, true)}
          </Box>
          <Box className={styles.fieldsColumn}>
            {renderFieldsList(secondColumnFields, true)}
          </Box>
        </Box>
      );
    }

    return (
      <Box
        className={clsx(
          singleColumn && styles.fieldsGridSingle,
          fullWidth && !singleColumn && styles.fieldsGridWide
        )}
      >
        {renderFieldsList(fields, singleColumn || fullWidth)}
      </Box>
    );
  };

  return (
    <Box
      className={clsx(
        styles.infoCardWrapper,
        fullWidth && styles.infoCardFull,
        autoHeight && styles.infoCardWrapperAuto
      )}
      dir={textDirection}
    >
      <Typography className={styles.cardTitle}>{t(titleKey)}</Typography>
      <Box
        className={clsx(styles.infoCard, autoHeight && styles.infoCardAuto)}
        dir={textDirection}
      >
        {renderFields()}
      </Box>
    </Box>
  );
};
